import React, { useState } from "react";
import {
  Typography,
  Box,
  Stack,
  FormControlLabel,
  Radio,
  useTheme,
} from "@mui/material";
import CustomInput from "global/components/CustomInput/CustomInput";
import { AppModal } from "global/components/AppModal";
import MyTemplates from "screens/Templates/MyTemplates";
import { fillCopy, openErrorNotification } from "helpers/methods";
import history from "utils/history";
import strings from "global/constants/StringConstants";
import FeatureKeys from "global/constants/FeatureKeys";
import { useFeatureEnabled } from "hooks/use-feature";
import { hasAccessTo } from "layouts/Config";
import { hideAiSession, listAiSessions } from "./AiBuilder/AiWebFormService";
import { aiBuilderPath, aiErrorMessage } from "./AiBuilder/AiWebFormHelper";
import { AiSessionSummary } from "./AiBuilder/AiWebFormTypesAndValidation";
import { AiBuildChoice, BuildChoice } from "./AiBuilder/components/AiBuildChoice";
import { LayoutGlyph, SparkleGlyph } from "./AiBuilder/components/AiWebFormIcons";
import aiWebFormTokens from "./AiBuilder/AiWebForm.tokens";

const aiCopy = strings.aiWebForm;

interface CreateWebFormModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: (data: any) => void;
  initialTemplate?: any;
}

type ModalStep =
  | "START"
  | "SELECT_TEMPLATE"
  | "SELECT_SIGNERS"
  | "NAME_FORM"
  | "BUILD_CHOICE";

export const CreateWebFormModal: React.FC<CreateWebFormModalProps> = ({
  open,
  onClose,
  onComplete,
  initialTemplate,
}) => {
  const theme = useTheme();
  const [currentStep, setCurrentStep] = useState<ModalStep>(
    initialTemplate ? "SELECT_SIGNERS" : "START",
  );

  const [startMode, setStartMode] = useState<"TEMPLATE" | "SCRATCH" | null>(
    initialTemplate ? "TEMPLATE" : null,
  );
  const [selectedTemplate, setSelectedTemplate] = useState<any | null>(
    initialTemplate || null,
  );
  const [selectedSigners, setSelectedSigners] = useState<number[]>([]);
  const [formName, setFormName] = useState<string>("");
  // Build with AI: offered after the name, when the plan has the AI assistant
  // and the role may create on templates.
  const aiAvailable =
    useFeatureEnabled(FeatureKeys.AI_ASSISTANT) &&
    hasAccessTo(strings.resources.template, strings.addPermission);
  const [buildChoice, setBuildChoice] = useState<BuildChoice>("ai");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiExisting, setAiExisting] = useState<AiSessionSummary | null>(null);
  const [aiChecking, setAiChecking] = useState(false);
  const [aiRemoving, setAiRemoving] = useState(false);

  React.useEffect(() => {
    if (open) {
      if (initialTemplate) {
        setCurrentStep("SELECT_SIGNERS");
        setStartMode("TEMPLATE");
        setSelectedTemplate(initialTemplate);
      } else {
        setCurrentStep("START");
        setStartMode(null);
        setSelectedTemplate(null);
      }
      setSelectedSigners([]);
      setFormName("");
      setBuildChoice("ai");
      setAiPrompt("");
      setAiExisting(null);
    }
  }, [open, initialTemplate]);

  const handleClose = () => {
    setCurrentStep(initialTemplate ? "SELECT_SIGNERS" : "START");
    setStartMode(initialTemplate ? "TEMPLATE" : null);
    setSelectedTemplate(initialTemplate || null);
    setSelectedSigners([]);
    setFormName("");
    setBuildChoice("ai");
    setAiPrompt("");
    setAiExisting(null);
    onClose();
  };

  const signerId = selectedSigners.length > 0 ? selectedSigners[0] : 1;

  /** The drag-and-drop editor, as before Build with AI existed. */
  const completeWithoutAi = () => {
    const payload = {
      mode: startMode,
      templateId: selectedTemplate?.id,
      signers: selectedSigners,
      name: formName,
    };
    if (startMode === "TEMPLATE" && selectedTemplate) {
      localStorage.setItem(
        `webform_template_${selectedTemplate.id}`,
        JSON.stringify(selectedTemplate),
      );
    }
    onComplete(payload);
    handleClose();
  };

  const openAiBuilder = (sessionId: string | null, prompt?: string) => {
    history.push(
      aiBuilderPath({
        sessionId,
        templateId: selectedTemplate?.id,
        signerId,
        name: formName.trim(),
      }),
      { prompt: prompt?.trim() || undefined },
    );
    handleClose();
  };

  /** The build choice opens on the design this template signer already has, if any. */
  const showBuildChoice = async () => {
    setCurrentStep("BUILD_CHOICE");
    setBuildChoice("ai");
    setAiExisting(null);
    setAiChecking(true);
    try {
      const list = await listAiSessions({
        templateId: selectedTemplate?.id,
        signerId,
        limit: 1,
      });
      setAiExisting(list.sessions[0] ?? null);
    } catch {
      // No list, no "Continue" card: starting a new design still works.
    } finally {
      setAiChecking(false);
    }
  };

  const removeExisting = async (session: AiSessionSummary) => {
    setAiRemoving(true);
    try {
      await hideAiSession(session.sessionId);
      setAiExisting(null);
    } catch (error: any) {
      openErrorNotification(aiErrorMessage(error) || aiCopy.loadFailed);
    } finally {
      setAiRemoving(false);
    }
  };

  const handleNext = () => {
    if (currentStep === "START") {
      if (startMode === "TEMPLATE") setCurrentStep("SELECT_TEMPLATE");
      else setCurrentStep("NAME_FORM");
    } else if (currentStep === "SELECT_SIGNERS") {
      setCurrentStep("NAME_FORM");
    } else if (currentStep === "NAME_FORM") {
      if (aiAvailable && startMode === "TEMPLATE" && selectedTemplate) {
        showBuildChoice();
      } else {
        completeWithoutAi();
      }
    } else if (currentStep === "BUILD_CHOICE") {
      if (buildChoice === "ai") openAiBuilder(null, aiPrompt);
      else completeWithoutAi();
    }
  };

  const handleBack = () => {
    if (currentStep === "SELECT_TEMPLATE") setCurrentStep("START");
    else if (currentStep === "SELECT_SIGNERS") {
      if (initialTemplate) handleClose();
      else setCurrentStep("SELECT_TEMPLATE");
    } else if (currentStep === "NAME_FORM") {
      if (startMode === "TEMPLATE") setCurrentStep("SELECT_SIGNERS");
      else setCurrentStep("START");
    } else if (currentStep === "BUILD_CHOICE") {
      setCurrentStep("NAME_FORM");
    }
  };

  const renderStartBody = () => (
    <Box sx={{ p: 1, minWidth: { xs: "auto", sm: 400 } }}>
      <Stack spacing={2} role="radiogroup" aria-label="How to start">
        <Box
          onClick={() => setStartMode("TEMPLATE")}
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            border: "1px solid",
            borderColor:
              startMode === "TEMPLATE" ? theme.palette.primary.main : "#e0e0e0",
            boxShadow:
              startMode === "TEMPLATE"
                ? `0 0 0 1px ${theme.palette.primary.main}`
                : "none",
            borderRadius: 2,
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            bgcolor: startMode === "TEMPLATE" ? "#f8f9fa" : "transparent",
          }}
        >
          <Radio
            checked={startMode === "TEMPLATE"}
            onChange={() => setStartMode("TEMPLATE")}
            color="primary"
            inputProps={{ "aria-label": "Use Template" }}
          />
          <Box ml={1}>
            <Typography variant="subtitle2" fontWeight={600}>
              Use Template
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create a form based on an existing template
            </Typography>
          </Box>
        </Box>

        <Box
          // onClick={() => setStartMode('SCRATCH')} // Temporarily disabled
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            border: "1px solid #e0e0e0",
            boxShadow: "none",
            borderRadius: 2,
            cursor: "not-allowed",
            opacity: 0.5,
            bgcolor: "transparent",
          }}
        >
          <Radio
            checked={false}
            disabled
            color="primary"
            inputProps={{ "aria-label": "Start from scratch (coming soon)" }}
          />
          <Box ml={1}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
            >
              Start from Scratch (Coming Soon)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Build a new form from the ground up
            </Typography>
          </Box>
        </Box>
      </Stack>
    </Box>
  );

  const handleTemplateSelection = (template: any) => {
    const validSigners =
      template.signers?.filter(
        (s: any) =>
          !s.cc &&
          (s.id > 0 ||
            (s.name && s.name.trim() !== "") ||
            (s.requestRole && s.requestRole.trim() !== "") ||
            (s.email && s.email.trim() !== "")),
      ) || [];
    const hasSigners = validSigners.length > 0;

    const hasFields =
      template.files &&
      template.files.some((file: any) => file.fields && file.fields.length > 0);

    if (!hasSigners) {
      openErrorNotification(
        "This template has no signers assigned. Please add signers to the template first.",
      );
      return;
    }

    if (!hasFields) {
      openErrorNotification(
        "This template has no fields. Web Forms require a template with fillable fields.",
      );
      return;
    }

    const hasMissingDataLabels =
      template.files &&
      template.files.some(
        (file: any) =>
          file.fields &&
          file.fields.some(
            (field: any) => !field.label || field.label.trim() === "",
          ),
      );

    if (hasMissingDataLabels) {
      openErrorNotification(
        "Some fields in this template are missing Data Labels. Please edit the template, assign Data Labels to all fields, and try again.",
      );
      return;
    }

    setSelectedTemplate(template);
    setSelectedSigners([]);
    setCurrentStep("SELECT_SIGNERS");
  };

  const renderSelectTemplateBody = () => (
    <Box
      sx={{
        p: 0,
        width: "100%",
        height: 500,
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <MyTemplates
        isShowCreateBtn={false}
        isShowMoreIcon={false}
        isUseTemplate={true}
        onTemplateSelect={handleTemplateSelection}
      />
    </Box>
  );

  const renderSelectSignersBody = () => {
    const availableSigners =
      selectedTemplate?.signers?.filter(
        (s: any) =>
          !s.cc &&
          (s.id > 0 ||
            (s.name && s.name.trim() !== "") ||
            (s.requestRole && s.requestRole.trim() !== "") ||
            (s.email && s.email.trim() !== "")),
      ) || [];

    return (
      <Box sx={{ p: 1, minWidth: { xs: "auto", sm: 400 }, textAlign: "left" }}>
        <Typography
          id="webform-signers-group-label"
          variant="body2"
          color="text.secondary"
          mb={2}
        >
          Choose the signers who need to complete this form before signing
        </Typography>
        {availableSigners.length > 0 ? (
          <Stack
            spacing={1}
            role="radiogroup"
            aria-labelledby="webform-signers-group-label"
          >
            {availableSigners.map((signer: any, index: number) => {
              const roleKey =
                signer.requestRole || signer.name || `Role ${index + 1}`;
              const signerId = Number(signer.id);
              const email = signer.email;
              const order = signer.order;
              return (
                <Stack
                  key={signer.id ?? signer.requestRole ?? index}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                >
                  {order ? (
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: 1,
                        backgroundColor:
                          signer.color || theme.palette.primary.main,
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        flexShrink: 0,
                      }}
                    >
                      {order}
                    </Box>
                  ) : null}
                  <Box
                    sx={{
                      flex: 1,
                      p: 1,
                      border: "1px solid #eee",
                      borderRadius: 1,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Radio
                          size="small"
                          color="primary"
                          name="webform-signer-selection"
                          checked={selectedSigners.includes(signerId)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedSigners([signerId]);
                            }
                          }}
                        />
                      }
                      label={
                        <Stack direction="column" spacing={0.25} ml={1}>
                          <Typography
                            variant="body1"
                            color="text.primary"
                            fontWeight={500}
                          >
                            {roleKey}
                          </Typography>
                          {email && (
                            <Typography variant="body2" color="text.secondary">
                              {email}
                            </Typography>
                          )}
                        </Stack>
                      }
                      sx={{ m: 0, alignItems: "flex-start" }}
                    />
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No roles or signers found in this template.
          </Typography>
        )}
      </Box>
    );
  };

  const renderNameFormBody = () => {
    return (
      <Box sx={{ p: 1, minWidth: { xs: "auto", sm: 400 }, textAlign: "left" }}>
        <CustomInput
          label="Form Name"
          required
          value={formName}
          onChange={(e: any) => {
            if (e.target.value.length <= 200) {
              setFormName(e.target.value);
            }
          }}
          placeHolder="Enter web form name"
        />
        <Stack direction="row" justifyContent="flex-end" mt={0.5} px={0.5}>
          <Typography variant="caption" color="text.secondary">
            {formName.length}/200
          </Typography>
        </Stack>
      </Box>
    );
  };

  const getTitle = () => {
    switch (currentStep) {
      case "START":
        return "Create Web Form";
      case "SELECT_TEMPLATE":
        return "Select Template";
      case "SELECT_SIGNERS":
        return "Choose Signers";
      case "NAME_FORM":
        return "Name Your Web Form";
      case "BUILD_CHOICE":
        return aiCopy.choiceTitle;
      default:
        return "";
    }
  };

  const getBody = () => {
    switch (currentStep) {
      case "START":
        return renderStartBody();
      case "SELECT_TEMPLATE":
        return renderSelectTemplateBody();
      case "SELECT_SIGNERS":
        return renderSelectSignersBody();
      case "NAME_FORM":
        return renderNameFormBody();
      case "BUILD_CHOICE":
        return (
          <AiBuildChoice
            existing={aiExisting}
            checking={aiChecking}
            choice={buildChoice}
            onChoice={setBuildChoice}
            prompt={aiPrompt}
            onPrompt={setAiPrompt}
            onContinueExisting={(session) => openAiBuilder(session.sessionId)}
            onRemoveExisting={removeExisting}
            onStartNew={() => setAiExisting(null)}
            onWithoutAi={completeWithoutAi}
            removing={aiRemoving}
          />
        );
      default:
        return <Box />;
    }
  };

  const isBuildChoice = currentStep === "BUILD_CHOICE";
  const choiceSigner = selectedTemplate?.signers?.find(
    (s: any) => Number(s.id) === signerId,
  );
  const signerName = choiceSigner?.requestRole || choiceSigner?.name || "";
  const templateName = selectedTemplate?.templateName ?? selectedTemplate?.name ?? "";

  const getPrimaryLabel = () => {
    if (currentStep === "SELECT_TEMPLATE") return undefined;
    if (isBuildChoice) {
      // With a design to continue, the cards act on their own (01b).
      if (aiExisting || aiChecking) return undefined;
      return buildChoice === "ai" ? (
        <Box
          component="span"
          sx={{ display: "inline-flex", alignItems: "center", gap: "8px", "& svg": { width: 16, height: 16 } }}
        >
          <SparkleGlyph />
          {aiCopy.buildWithAi}
        </Box>
      ) : (
        aiCopy.continueButton
      );
    }
    if (currentStep === "NAME_FORM") {
      return aiAvailable && startMode === "TEMPLATE" ? "Next" : "Create";
    }
    return "Next";
  };

  return (
    <AppModal
      open={open}
      onClose={handleClose}
      title={getTitle()}
      subtitle={
        isBuildChoice && templateName
          ? signerName
            ? fillCopy(aiCopy.choiceSubtitle, { template: templateName, signer: signerName })
            : templateName
          : undefined
      }
      icon={isBuildChoice ? <LayoutGlyph /> : undefined}
      // The template picker needs the wide step, matching the other
      // "choose a template" entry points; the rest are short forms.
      size={currentStep === "SELECT_TEMPLATE" ? "xl" : "md"}
      width={isBuildChoice ? aiWebFormTokens.layout.dialogWidth : undefined}
      primaryLabel={getPrimaryLabel()}
      onPrimary={handleNext}
      primaryDisabled={
        (currentStep === "START" && !startMode) ||
        (currentStep === "SELECT_SIGNERS" && selectedSigners.length === 0) ||
        (currentStep === "NAME_FORM" && formName.trim().length === 0)
      }
      cancelLabel={currentStep === "START" ? "Cancel" : "Back"}
      onCancel={currentStep === "START" ? handleClose : handleBack}
    >
      {getBody()}
    </AppModal>
  );
};
